import React from "react"
import { DateTime } from "luxon"
import {
  EVENT_TYPES,
  VACATION_APPROVED_STATUS,
  VACATION_REQUEST_STATUS,
  VACATION_STATES,
} from "../consts/modules/vacations.consts"
import VacationFlag from "../components/molecules/VacationFlag.molecule"
import {
  EmptyVacationDayBadge,
  NonEmptyVacationDayBadge,
} from "../components/molecules/CellReportBadges.molecule"
import {
  getAntiquityFromDate,
  getValidLuxonDate,
  isDateAtWeekend,
} from "./date.utils"

/** @typedef {import("../services/vacation.service.types").getTeamVacationsRequestsByYearResponse} TeamVacationRequest */
/** @typedef {typeof VACATION_STATES[keyof typeof VACATION_STATES]} vacationStates */

/** @type {(vacationRequest: TeamVacationRequest) => vacationStates} */
export const getVacationState = (vacationRequest) => {
  if (isVacationRequestApprovedAndPendingPlusDaysPayDate(vacationRequest)) {
    return VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE
  }
  if (isVacationRequestApproved(vacationRequest)) {
    return VACATION_STATES.APPROVED
  }
  if (isVacationRequestBlocked(vacationRequest)) {
    return VACATION_STATES.BLOCKED
  }
  if (isVacationChangeRequest(vacationRequest)) {
    return VACATION_STATES.CHANGE_REQUEST
  }
  if (isVacationCancelRequest(vacationRequest)) {
    return VACATION_STATES.CANCEL_REQUEST
  }
  if (isVacationRequestPending(vacationRequest)) {
    return VACATION_STATES.PENDING
  }
  if (isVacationRequestRejected(vacationRequest)) {
    return VACATION_STATES.REJECTED
  }
  if (isVacationRequestCanceled(vacationRequest)) {
    return VACATION_STATES.CANCELED
  }
  return VACATION_STATES.BLOCKED
}

/** @type {(vacationRequest: TeamVacationRequest) => boolean} */
export const isVacationRequestBlocked = (vacationRequest) => {
  return Boolean(
    vacationRequest.active === 1 && vacationRequest.vacationsHaveBeenCounted
  )
}

/** @type {(vacationRequest: TeamVacationRequest) => boolean} */
export const isVacationRequestPending = (vacationRequest) => {
  return Boolean(
    vacationRequest.requestStatus === VACATION_REQUEST_STATUS.NOT_REVIEWED &&
      vacationRequest.approved === VACATION_APPROVED_STATUS.NOT_REVIEWED &&
      !vacationRequest.vacationsHaveBeenCounted &&
      !vacationRequest?.approver
  )
}

/** @type {(vacationRequest: TeamVacationRequest) => boolean} */
export const isVacationChangeRequest = (vacationRequest) => {
  return Boolean(
    vacationRequest.changeRequest &&
      !vacationRequest?.approver &&
      vacationRequest.approved === null
  )
}

/** @type {(vacationRequest: TeamVacationRequest) => boolean} */
export const isVacationCancelRequest = (vacationRequest) => {
  return Boolean(
    vacationRequest.cancelRequest &&
      !vacationRequest?.approver &&
      vacationRequest.approved === null
  )
}

/** @type {(vacationRequest: TeamVacationRequest) => boolean} */
export const isVacationRequestApproved = (vacationRequest) => {
  return Boolean(
    vacationRequest.approved === VACATION_APPROVED_STATUS.APPROVED &&
      vacationRequest?.approver
  )
}

/** @type {(vacationRequest: TeamVacationRequest) => boolean} */
export const isVacationRequestApprovedAndPendingPlusDaysPayDate = (
  vacationRequest
) => {
  return Boolean(
    isVacationRequestApproved(vacationRequest) &&
      vacationRequest.plusDaysPayDate === null &&
      vacationRequest.numberOfPlusDays > 0
  )
}

/** @type {(vacationRequest: TeamVacationRequest) => boolean} */
export const isVacationRequestRejected = (vacationRequest) => {
  return Boolean(
    vacationRequest.approved === VACATION_APPROVED_STATUS.REJECTED &&
      vacationRequest?.approver &&
      vacationRequest.cancelationMotive
  )
}

/** @type {(vacationRequest: TeamVacationRequest) => boolean} */
export const isVacationRequestCanceledByAdmin = (vacationRequest) => {
  return Boolean(
    vacationRequest.approved === VACATION_APPROVED_STATUS.CANCELED_BY_ADMIN &&
      vacationRequest?.approver &&
      vacationRequest.cancelationMotive
  )
}

/** @type {(vacationRequest: TeamVacationRequest) => boolean} */
export const isVacationRequestCanceledByTeamMemberRequest = (
  vacationRequest
) => {
  return Boolean(
    vacationRequest.approved ===
      VACATION_APPROVED_STATUS.CANCELED_BY_TEAM_MEMBER &&
      vacationRequest.cancelationMotive
  )
}

/** @type {(vacationRequest: TeamVacationRequest) => boolean} */
export const isVacationRequestCanceled = (vacationRequest) => {
  return Boolean(
    isVacationRequestCanceledByAdmin(vacationRequest) ||
      isVacationRequestCanceledByTeamMemberRequest(vacationRequest)
  )
}

/** @type {(vacationRequest: TeamVacationRequest) => string} */
export const getVacationTooltip = (vacationRequest) => {
  if (
    vacationRequest.active === 0 &&
    vacationRequest.vacationsHaveBeenCounted
  ) {
    return ""
  }
  if (vacationRequest.cancelRequest) {
    return "Solicitud de cancelación pendiente"
  }
  if (vacationRequest.changeRequest) {
    return "Solicitud de cambio pendiente"
  }
  if (
    vacationRequest.numberOfPlusDays > 0 &&
    !vacationRequest.plusDaysPayedOff &&
    isVacationRequestApproved(vacationRequest)
  ) {
    return "Pago de Días+ por definir"
  }
  switch (getVacationState(vacationRequest)) {
    case VACATION_STATES.PENDING:
      return ""
    case VACATION_STATES.APPROVED:
      return `Aprobada por ${vacationRequest.approver?.fullName}`
    case VACATION_STATES.REJECTED:
      return `Rechazada por ${vacationRequest.approver?.fullName}`
    case VACATION_STATES.BLOCKED:
      return "Bloqueada"
    case VACATION_STATES.CANCELED:
      return `Cancelada por ${vacationRequest.approver?.fullName}`
    default:
      return ""
  }
}

/** @type {(request: TeamVacationRequest) => number} */
export const getVacationRequestPriority = (request) => {
  const isHoliday =
    request.eventType !== EVENT_TYPES.VACATION &&
    !Object.keys(request).includes("groupedEvents")
  const isPast = isVacationRequestBlocked(request)
  const isPendingApproval = isVacationRequestPending(request)
  const isCancelRequest = request.cancelRequest
  const isChangeRequest = request.changeRequest
  const isPlusDaysUnpaid =
    !request.plusDaysPayDate && request.numberOfPlusDays > 0
  const isApproved = isVacationRequestApproved(request)
  const isRejected = isVacationRequestRejected(request)
  const isCanceled = isVacationRequestCanceled(request)
  if (isHoliday) return 0
  if (isPast) return 8
  if (isApproved) return 5
  if (isRejected) return 6
  if (isCanceled) return 7
  if (isCancelRequest) return 2
  if (isChangeRequest) return 3
  if (isPendingApproval) return 1
  if (isPlusDaysUnpaid) return 4
  return 99
}

/** @type {(idHolidaySchema: number) => boolean} */
export const isMexicanSchema = (idHolidaySchema) => {
  return idHolidaySchema === 1 || idHolidaySchema === 2
}

/** @type {(idHolidaySchema: number) => boolean} */
export const isForeignSchema = (idHolidaySchema) => {
  return idHolidaySchema === 3
}

export const getVacationRangeLabel = (
  numberOfDays,
  startDate,
  endDate,
  options = {
    includeNumberOfDays: true,
    includeDateRange: true,
    dateFormat: "dd/MM/yyyy",
  }
) => {
  const numberOfDaysLabel = `${numberOfDays} día${numberOfDays > 1 ? "s" : ""}`
  let dateRangeLabel = `${DateTime.fromISO(startDate?.split("T")[0]).toFormat(
    options.dateFormat
  )} - ${DateTime.fromISO(endDate?.split("T")[0]).toFormat(options.dateFormat)}`
  if (startDate === endDate) {
    dateRangeLabel = `${DateTime.fromISO(startDate?.split("T")[0]).toFormat(
      options.dateFormat
    )}`
  }
  if (!options.includeNumberOfDays && !options.includeDateRange) {
    return ""
  }
  if (!options.includeNumberOfDays) {
    return dateRangeLabel
  }
  if (!options.includeDateRange) {
    return numberOfDaysLabel
  }
  return `${numberOfDaysLabel} (${dateRangeLabel})`
}

export const isVacationValidForPlusDaysPayDate = (vacationRequest) => {
  return vacationRequest && vacationRequest.numberOfPlusDays > 0
}

export const vacationEventHasNotification = (event) => {
  if (isVacationRequestCanceledByTeamMemberRequest(event)) {
    return false
  }
  return (
    event?.changeRequest ||
    event?.cancelRequest ||
    (event.numberOfPlusDays > 0 &&
      !event.plusDaysPayedOff &&
      !(event.active === 0 && event.vacationsHaveBeenCounted) &&
      isVacationRequestApproved(event))
  )
}

export const isEventHolidayOfAnyType = (event) => {
  return (
    event.eventType === EVENT_TYPES.HOLIDAY ||
    event.eventType === EVENT_TYPES.GLOBAL_SISU ||
    event.eventType === EVENT_TYPES.DAY_OFF
  )
}

export const isEventPeriodDelimiter = (event) => {
  return (
    event.eventType === EVENT_TYPES.PERIOD_START ||
    event.eventType === EVENT_TYPES.PERIOD_END
  )
}

/**
 * Utility function to check if the holiday is available for the team member.
 *
 * @param {number} holidaySchemaType
 * @param {Array} teamMemberSchemaRules
 * @returns {boolean}
 */
export const isHolidaySchemaSameAsTeamMember = (
  holidaySchemaType,
  teamMemberSchemaRules
) => {
  const existingRule = teamMemberSchemaRules?.find(
    (rule) => rule.idTypeOfHoliday === holidaySchemaType
  )
  return Boolean(holidaySchemaType === 3 || existingRule)
}

/**
 * Utility function to render the day off icon. Current cases are:
 * - Empty vacations
 * - Non-empty vacations
 * - Holidays
 * - Base case (calendar icon) or null
 *
 * @param {Object} params
 * @param {Object} params.rowData
 * @param {boolean} params.rowData.isHoliday
 * @param {boolean} params.rowData.isVacationDay
 * @param {boolean} params.rowData.hasTasksInDate
 * @param {number} params.rowData.holidaySchemaType
 * @param {string} params.rowData.holidaySchemaName
 * @param {string} params.rowData.holidayMotive
 * @param {string} params.rowData.startDate
 * @param {string} params.rowData.endDate
 * @param {string} params.rowData.vacationsMotive
 * @param {string} params.rowData.workDayComment
 *
 * @param {boolean} [params.showBaseCase=true] - If true, show the base case icon (calendar).
 *
 * @returns {JSX.Element|null}
 */
export const renderDayOffIcon = ({ rowData, showBaseCase = true }) => {
  if (rowData?.isHoliday) {
    return (
      <VacationFlag
        schemaTitle={rowData.holidaySchemaName}
        tooltipConfig={{
          content: rowData.name,
          position: "right",
        }}
        showTooltip
      />
    )
  }
  if (rowData?.isVacationDay && !rowData?.hasTasksInDate) {
    return (
      <EmptyVacationDayBadge
        vacationsDateRange={[rowData?.startDate, rowData?.endDate]}
        vacationsMotive={rowData?.vacationsMotive}
      />
    )
  }
  if (rowData?.isVacationDay && rowData?.hasTasksInDate) {
    return (
      <NonEmptyVacationDayBadge vacationsMotive={rowData?.workDayComment} />
    )
  }
  if (showBaseCase) {
    return <i className="pi pi-calendar"></i>
  }
  return null
}

/**
 * Utility function to show the cell value in the weekly report.
 *
 * @param {Object} currentDay
 * @param {number} currentDay.totalSisuTime
 * @param {boolean} currentDay.isVacationDay
 * @returns {number|null|string}
 */
export const showWeeklyReportCellValue = (currentDay) => {
  const isEmptyVacationDay =
    currentDay?.totalSisuTime === 0 && currentDay?.isVacationDay
  const isEmptyDay =
    currentDay?.totalSisuTime === 0 && !currentDay?.isVacationDay
  if (isEmptyDay || !currentDay) {
    return "-"
  }
  if (isEmptyVacationDay) {
    return null
  }
  return currentDay?.totalSisuTime
}

/**
 *
 * @param {Object} params
 * @param {DateTime|Date|string|number} params.workAnniversaryDate - The work anniversary date of the team member. Expected format: "YYYY-MM-DD".
 * @param {string} params.idTypeOfHoliday - The type of holiday.
 * @param {DateTime|Date|string|number} params.currentDate
 * @param {Object} params.holidayRule - Holiday rule, expected to have the following properties
 * @param {number} params.holidayRule.limitRequiredYears - The number of years required to unlock the holiday.
 * @param {number} params.holidayRule.idTypeOfHoliday - The type of holiday.
 *
 * @returns {boolean}
 */
export const isHolidayUnlockedForTeamMember = ({
  workAnniversaryDate,
  idTypeOfHoliday,
  currentDate,
  holidayRule,
}) => {
  let isHolidayAvailableForTeamMember = true
  if (
    workAnniversaryDate &&
    holidayRule?.limitRequiredYears &&
    idTypeOfHoliday === holidayRule?.idTypeOfHoliday
  ) {
    const teamMemberAntiquity = getAntiquityFromDate({
      fromDate: workAnniversaryDate,
      toDate: currentDate,
    })
    isHolidayAvailableForTeamMember =
      idTypeOfHoliday === holidayRule?.idTypeOfHoliday &&
      teamMemberAntiquity.years >= holidayRule?.limitRequiredYears
  }
  return isHolidayAvailableForTeamMember
}

export const getRuleFromSchemaRules = ({ schemaRules, idTypeOfHoliday }) => {
  return schemaRules?.find(
    (rule) => parseInt(rule.idTypeOfHoliday) === parseInt(idTypeOfHoliday)
  )
}

/**
 * Determines if a holiday is available for a team member.
 *
 * @param {Object} params
 * @param {number} params.idTypeOfHoliday - The holiday type to evaluate.
 * @param {number} params.idHolidaySchemaType - Schema type used for comparison.
 * @param {Array} params.teamMemberSchemaRules - Specific rules for the team member.
 * @param {boolean} [params.isHoliday=true] - Whether the current date is a holiday.
 * @param {string|Date|DateTime} params.workAnniversaryDate - Work anniversary of the team member.
 * @param {string|Date|DateTime} params.currentDate - The date to evaluate.
 *
 * @returns {boolean} Whether the holiday is unlocked for the team member.
 */
export const isHolidayAvailable = ({
  idTypeOfHoliday,
  idHolidaySchemaType,
  teamMemberSchemaRules,
  isHoliday = true,
  workAnniversaryDate,
  currentDate,
}) => {
  const isSameSchema = isHolidaySchemaSameAsTeamMember(
    idHolidaySchemaType,
    teamMemberSchemaRules
  )
  const holidayRule = getRuleFromSchemaRules({
    schemaRules: teamMemberSchemaRules,
    idTypeOfHoliday,
  })
  return (
    isHoliday &&
    isSameSchema &&
    isHolidayUnlockedForTeamMember({
      workAnniversaryDate,
      idTypeOfHoliday,
      holidayRule,
      currentDate,
    })
  )
}

export const hasAvailableVacationDays = (daysResume) => {
  const hasBucketDays =
    daysResume.availableBucketDays !== null &&
    daysResume.availableBucketDays > -3
  const hasPlusDays =
    daysResume.availablePlusDays !== null && daysResume.availablePlusDays > 0
  const hasAccordedDays =
    daysResume.availableAccordedDays !== null &&
    daysResume.availableAccordedDays > 0
  const hasAdditionalDays =
    daysResume.availableAdditionalDays !== null &&
    daysResume.availableAdditionalDays > 0
  const hasAvailableDays =
    hasBucketDays || hasPlusDays || hasAccordedDays || hasAdditionalDays
  return hasAvailableDays
}

export const generatePeriodStartEvent = (date) => {
  return {
    id: -1,
    start: date.toJSDate(),
    end: date.toJSDate(),
    date: date?.toISO(),
    createdAt: DateTime.now().toISO(),
    title: "Inicio de periodo",
    eventType: "periodStart",
    status: "periodStart",
  }
}

export const generatePeriodEndEvent = (date) => {
  return {
    id: -2,
    start: date.toJSDate(),
    end: date.toJSDate(),
    date: date?.toISO(),
    createdAt: DateTime.now().toISO(),
    title: "Fin de periodo",
    eventType: "periodEnd",
    status: "periodEnd",
  }
}

export const getNumberOfTypeDaysToUse = ({
  holidaySchemaId,
  teamMemberVacationResume,
  availableBucketDays,
}) => {
  let typesOfDaysToUse = 0
  if (isMexicanSchema(holidaySchemaId) && availableBucketDays > -3) {
    typesOfDaysToUse++
  }
  if (
    teamMemberVacationResume.availablePlusDays !== null &&
    teamMemberVacationResume.availablePlusDays > 0
  ) {
    typesOfDaysToUse++
  }
  if (
    teamMemberVacationResume.availableAccordedDays !== null &&
    teamMemberVacationResume.availableAccordedDays > 0
  )
    typesOfDaysToUse++
  if (
    teamMemberVacationResume.availableAdditionalDays !== null &&
    teamMemberVacationResume.availableAdditionalDays > 0
  )
    typesOfDaysToUse++
  return typesOfDaysToUse
}

export const getClosestFutureBusinessDay = (
  startDate,
  disabledDayList = []
) => {
  let date = startDate.set({ hour: 12 })
  const disabledISOStrings = disabledDayList.map((day) =>
    getValidLuxonDate(day.date).toISODate()
  )
  while (
    isDateAtWeekend(date) ||
    disabledISOStrings.includes(date.toISODate())
  ) {
    date = date.plus({ days: 1 })
  }
  return date.set({ hour: 12, millisecond: 0, minute: 0, second: 0 })
}

export const availableSingleDays = (
  availableDaysList,
  vacationRequestValues
) => {
  if (
    availableDaysList.accordedDays !== null &&
    availableDaysList.accordedDays > 0
  ) {
    return availableDaysList.accordedDays - vacationRequestValues.accordedDays
  } else if (
    availableDaysList.plusDays !== null &&
    availableDaysList.plusDays > 0
  ) {
    return availableDaysList.plusDays - vacationRequestValues.plusDays
  } else if (
    availableDaysList.additionalDays !== null &&
    availableDaysList.additionalDays > 0
  ) {
    return (
      availableDaysList.additionalDays - vacationRequestValues.additionalDays
    )
  } else if (
    availableDaysList.bucketDays !== null &&
    availableDaysList.bucketDays > -3
  ) {
    return availableDaysList.bucketDays - vacationRequestValues.bucketDays
  }
}

/**
 *
 * @param {Object} bucketDaysList
 * @param {Array} bucketDaysList.data
 * @param {DateTime} periodEnd
 * @returns {DateTime}
 */
export const getMaxStartDate = (bucketDaysList, periodEnd) => {
  if (!bucketDaysList?.data?.length) {
    return DateTime.local()
  }
  return DateTime.local().set({
    year: bucketDaysList?.data?.[0]?.monthsData[
      bucketDaysList?.data?.[0]?.monthsData.length - 1
    ].year,
    month:
      bucketDaysList?.data?.[0]?.monthsData[
        bucketDaysList?.data?.[0]?.monthsData.length - 1
      ].month,
    day: periodEnd.day,
  })
}

/**
 * Convierte el aniversario a DateTime local.
 * Acepta un DateTime ya construido o una cadena; en cadenas toma solo la parte YYYY-MM-DD,
 * para que no se corra de día al interpretarla como UTC.
 *
 * @param {string|DateTime|null|undefined} anniversaryDate
 * @returns {DateTime|null}
 */
export const toLocalAnniversaryDateTime = (anniversaryDate) => {
  if (!anniversaryDate) return null
  if (DateTime.isDateTime(anniversaryDate)) return anniversaryDate

  const parsed = DateTime.fromISO(String(anniversaryDate).split("T")[0])
  return parsed.isValid ? parsed : null
}

/**
 * Calculates whether a given column index corresponds to a work-anniversary month
 * within a chronological range, and determines the anniversary cycle number.
 *
 * @param {number} index - The position of the current month within the range.
 * @param {Array<{month: number, year: number}>} rangeToConsider - Sequential list of months to evaluate.
 * @param {number} anniversaryMonth - Object date.
 *
 * @returns {{ isAnniversary: boolean, anniversaryNumber: number|null }}
 * - `isAnniversary`: Whether the current index corresponds to the anniversary month.
 * - `anniversaryNumber`: Work year that starts that month (1 first year, 2 second, …), or `null`.
 */
export function getAnniversaryInfoFromRange(
  index,
  rangeToConsider,
  anniversaryDate
) {
  const anniversary = toLocalAnniversaryDateTime(anniversaryDate)
  if (!anniversary) return { isAnniversary: false, anniversaryNumber: null }

  const startYear = anniversary.year
  const startMonth = anniversary.month

  const cell = rangeToConsider[index]
  if (!cell) return { isAnniversary: false, anniversaryNumber: null }

  if (cell.month !== startMonth) {
    return { isAnniversary: false, anniversaryNumber: null }
  }

  const yearsSinceStart = cell.year - startYear

  if (yearsSinceStart < 0) {
    return { isAnniversary: false, anniversaryNumber: null }
  }

  return {
    isAnniversary: true,
    anniversaryNumber: yearsSinceStart + 1,
  }
}

/* Processes a list of additional-day packages and returns a summary
 * with the next package to expire and whether any package is expiring soon.
 *
 * @param {Array} daysMessages - Array of objects representing additional-day packages.
 * Each item must include at least:
 *  - {number} availableDays
 *  - {string} motive
 *  - {string|Date} expirationDate (ISO)
 *  - {string} type
 *
 * @returns {Object} Summary of the additional-days status:
 * {
 *   hasExpiringSoon: boolean, // true if at least one package expires in <= 10 days
 *   nextExpiring: {
 *      availableDays: number,
 *      motive: string,
 *      expirationDate: string,
 *      daysUntilExpire: number
 *   } | null
 * }
 */
export const getAdditionalDaysSummary = (daysMessages = []) => {
  if (!Array.isArray(daysMessages) || daysMessages.length === 0) {
    return {
      hasExpiringSoon: false,
      nextExpiring: null,
    }
  }

  const normalize = (list) =>
    list.map((m) => ({
      ...m,
      _expiration: getValidLuxonDate(m.expirationDate).startOf("day"),
    }))

  const sortByExpiration = (list) =>
    [...list].sort((a, b) => {
      const diff = a._expiration - b._expiration
      if (diff !== 0) return diff

      return b.availableDays - a.availableDays
    })

  const daysRemaining = (luxonDate) => {
    const today = DateTime.now().startOf("day")
    const diff = luxonDate.diff(today, "days").days
    return Math.max(0, Math.ceil(diff))
  }

  const expiresSoon = (list, threshold = 10) =>
    list.some((msg) => daysRemaining(msg._expiration) <= threshold)

  const normalized = normalize(daysMessages)
  const sorted = sortByExpiration(normalized)
  const next = sorted[0]

  return {
    hasExpiringSoon: expiresSoon(normalized),
    nextExpiring: {
      availableDays: next.availableDays,
      motive: next.motive ?? next.type ?? "-",
      expirationDate: next._expiration.toFormat("dd/LL/yyyy"),
      daysUntilExpire: daysRemaining(next._expiration),
    },
  }
}

export const additionalDaysValidity = (workAnniversary) => {
  const validWorkAnniversary = getValidLuxonDate(workAnniversary)
  const minDate = DateTime.now().plus({ days: 10 })
  const maxDate =
    validWorkAnniversary ?
      validWorkAnniversary.plus({ years: 2 }).minus({ days: 1 })
    : null
  return { minDate, maxDate }
}

export const getTeamMemberVacationsCalendarBounds = (workAnniversary) => {
  if (!workAnniversary) {
    return {
      lastYearPeriod: { start: null, end: null },
      nextYearPeriod: { start: null, end: null },
    }
  }

  const now = DateTime.now()
  const anniversaryDate = getValidLuxonDate(workAnniversary)

  const day = anniversaryDate.day
  const month = anniversaryDate.month

  const anniversaryThisYear = DateTime.fromObject({
    year: now.year,
    month,
    day,
  }).startOf("day")

  let currentPeriodStartYear = now.year

  if (now < anniversaryThisYear) {
    currentPeriodStartYear = now.year - 1
  }

  const createPeriod = (yearTarget) => {
    const start = DateTime.fromObject({
      year: yearTarget,
      month,
      day,
    }).startOf("day")

    const end = start.plus({ years: 1 }).minus({ days: 1 }).endOf("day")

    return { start: start.toJSDate(), end: end.toJSDate() }
  }

  return {
    lastYearPeriod: createPeriod(currentPeriodStartYear - 1),
    nextYearPeriod: createPeriod(currentPeriodStartYear + 1),
  }
}

export const getManagementVacationsCalendarBounds = () => {
  const now = DateTime.now()
  const currentYear = now.year
  const prevYear = currentYear - 1
  const nextYear = currentYear + 1

  const createPeriod = (yearTarget) => {
    const start = DateTime.fromObject({
      year: yearTarget,
      month: 1,
      day: 1,
    }).startOf("day")

    const end = DateTime.fromObject({
      year: yearTarget,
      month: 12,
      day: 31,
    }).endOf("day")

    return { start: start.toJSDate(), end: end.toJSDate() }
  }

  return {
    lastYearPeriod: createPeriod(prevYear),
    nextYearPeriod: createPeriod(nextYear),
  }
}

/**
 * If period start is detected to be the start of the period
 * of the user, it will return the end of the previous month
 * to match new plus day and accorded days rules.
 *
 * If not, we will return same period start to avoid
 * breaking legacy behavior.
 * @param {string|Date|DateTime|undefined} workAnniversary
 * @param {string|Date|DateTime|undefined} periodStart
 */
export function getTeamMembersStartOfPeriodMonth(workAnniversary, periodStart) {
  const workAnniversaryDate = getValidLuxonDate(workAnniversary)
  const periodStartDate = getValidLuxonDate(periodStart)

  if (!workAnniversaryDate || !periodStartDate) {
    return periodStart
  }

  if (!workAnniversaryDate.isValid || !periodStartDate.isValid) {
    return periodStart
  }

  const monthsAreTheSame = workAnniversaryDate.month === periodStartDate.month
  const daysAreTheSame = workAnniversaryDate.day === periodStartDate.day

  if (!monthsAreTheSame || !daysAreTheSame) return periodStart

  return periodStartDate.startOf("month").toJSDate()
}

/**
 * If period end is detected to be the end of the period
 * of the user, it will return the last day of the previous
 * month (at 02:00) to match new plus day and accorded days rules.
 *
 * If not, we will return same period end to avoid
 * breaking legacy behavior.
 * @param {string|Date|DateTime|undefined} workAnniversary
 * @param {string|Date|DateTime|undefined} periodEnd
 */
export function getTeamMembersEndOfPeriodMonth(workAnniversary, periodEnd) {
  const workAnniversaryDate = getValidLuxonDate(workAnniversary)
  const periodEndDate = getValidLuxonDate(periodEnd)

  if (!workAnniversaryDate || !periodEndDate) {
    return periodEnd
  }

  if (!workAnniversaryDate.isValid || !periodEndDate.isValid) {
    return periodEnd
  }

  const monthsAreTheSame = workAnniversaryDate.month === periodEndDate.month
  const daysAreTheSame = workAnniversaryDate.day === periodEndDate.day
  const periodEndDateIsJustBeforeWorkAnniversary =
    periodEndDate.plus({ days: 1 }).day === workAnniversaryDate.day
  const technicallyDaysAreTheSame =
    periodEndDateIsJustBeforeWorkAnniversary || daysAreTheSame

  if (!monthsAreTheSame || !technicallyDaysAreTheSame) return periodEnd

  return periodEndDate
    .minus({ months: 1 })
    .endOf("month")
    .set({ hour: 2 })
    .toJSDate()
}

/**
 * Previously, plus and accorded days were gained on the exact
 * day of the work anniversary. Now, it is gained on the first
 * day of the month of the work anniversary, so we set
 * the work anniversary to the last day of the previous month.
 *
 * @param {string|Date|DateTime|undefined} workAnniversary
 * @returns {DateTime}
 */
export function getValidWorkAnniversaryForDayOffValidity(workAnniversary) {
  const workAnniversaryDate = getValidLuxonDate(workAnniversary)
  return workAnniversaryDate.minus({ months: 1 }).endOf("month")
}
